import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import { MapPin } from "@phosphor-icons/react";
import "leaflet/dist/leaflet.css";

// Auto-fit bounds when the position set changes
function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions.length) return;
    const latlngs = positions.map((p) => [p.lat, p.lng]);
    if (latlngs.length === 1) {
      map.setView(latlngs[0], 11);
    } else {
      map.fitBounds(latlngs, { padding: [30, 30], maxZoom: 11 });
    }
  }, [positions, map]);
  return null;
}

export default function FleetMap({ positions }) {
  const [collapsed, setCollapsed] = useState(false);
  // Filter to only positions with valid coordinates
  const pts = useMemo(() => positions.filter((p) => typeof p.lat === "number" && typeof p.lng === "number"), [positions]);
  if (pts.length === 0) return null;
  const center = [pts[0].lat, pts[0].lng];

  return (
    <div className="px-8 pb-3" data-testid="ct-fleet-map">
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
          <MapPin size={14} weight="duotone" className="text-ps-blue" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-bold text-ps-deep">Map</h3>
          <span className="text-[10px] text-slate-400">{pts.length} location{pts.length === 1 ? "" : "s"}</span>
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="ct-fleet-map-toggle"
            className="ml-auto text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-ps-blue transition-colors"
          >
            {collapsed ? "show" : "hide"}
          </button>
        </div>
        {!collapsed && (
          <div style={{ height: 280 }} className="w-full">
            <MapContainer center={center} zoom={9} className="w-full h-full" scrollWheelZoom={false} attributionControl={true}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds positions={pts} />
              {pts.map((p) => (
                <CircleMarker
                  key={`${p.source}-${p.vehicle_number}`}
                  center={[p.lat, p.lng]}
                  radius={9}
                  pathOptions={{
                    color: p.online ? "#10b981" : "#94a3b8",
                    fillColor: p.online ? "#10b981" : "#94a3b8",
                    fillOpacity: 0.85,
                    weight: 2,
                  }}
                  data-testid={`ct-map-marker-${p.vehicle_number}`}
                >
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold text-base">{p.vehicle_number}</div>
                      {p.license_plate && <div className="text-slate-500 text-[11px] font-mono">{p.license_plate}</div>}
                      <div className="mt-1 text-slate-700">{p.address || "—"}</div>
                      <div className="mt-1 text-slate-500 text-[11px]">
                        {typeof p.speed_kmh === "number" && <>Speed: <b>{p.speed_kmh} km/h</b><br/></>}
                        {typeof p.fuel_level_pct === "number" && <>Fuel: <b className={p.fuel_level_pct < 25 ? "text-red-600" : ""}>{p.fuel_level_pct}%</b><br/></>}
                        {p.engine && <>Engine: <b>{p.engine}</b><br/></>}
                        {p.last_seen && <>Last seen: {p.last_seen}</>}
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        )}
      </div>
    </div>
  );
}
